import { mdiChevronLeft, mdiClose, mdiRobotOutline } from '@mdi/js';
import React, { useContext } from 'react';
import { formatDate } from '../../../services';
import { MessagingContext } from '../../../services/messaging/context';
import { Tooltip } from '../../base';
import { authorNickname, isEnterAuthor } from '../util';
import * as S from './chat-header.styles';
import { UserAvatar, UserAvatarLogo, UserAvatarLogoContainer } from '../message-list.styles';
import enterLogo from '../icons/enter-services-icon.svg';

interface IChatHeaderProps {
    onBack?: () => void;
}

const getInitials = (name: string): string => {
    if (!name) return '';
    const parts = name.trim().split(' ');
    if (parts.length >= 2) {
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
};

const formatCreated = (isoString: string | undefined): string => {
    if (!isoString) return '';
    try {
        return formatDate(isoString, 'MMM d, yyyy h:mm a');
    } catch {
        return '';
    }
};

export const ChatHeader: React.FunctionComponent<IChatHeaderProps> = ({ onBack }) => {
    const context = useContext(MessagingContext);
    const message = context.message;

    const handleCloseClick = (): void => {
        context.onMessageClose && context.onMessageClose();
    };

    const handleBackClick = (): void => {
        onBack && onBack();
    };

    const nickname = authorNickname(message as any);
    const initials = getInitials(nickname);
    const createdFormatted = formatCreated(message?.created);
    const enterSender = isEnterAuthor(message as any);

    return (
        <S.Header>
            {onBack && (
                <S.BackButton onClick={handleBackClick} type="button" variant="unstyled" aria-label="Back">
                    <S.BackIcon path={mdiChevronLeft} size={0.83} />
                </S.BackButton>
            )}
            <S.HeaderLabels>
                {message && (nickname || createdFormatted) && (
                    <>
                        {enterSender ? (
                            <Tooltip direction="top" title={<span>{nickname}</span>}>
                                <UserAvatarLogoContainer>
                                    <UserAvatarLogo src={enterLogo} alt={nickname} />
                                </UserAvatarLogoContainer>
                            </Tooltip>
                        ) : (
                            <Tooltip direction="top" title={<span>{nickname || 'Unknown'}</span>}>
                                <UserAvatar>{initials || '?'}</UserAvatar>
                            </Tooltip>
                        )}
                        <S.HeaderTextBlock>
                            <S.NameRow>
                                {nickname && <S.CreatedBy>{nickname}</S.CreatedBy>}
                                {enterSender && <S.AiBadge>AI</S.AiBadge>}
                            </S.NameRow>
                            {createdFormatted && (
                                <S.CreatedDate>
                                    {enterSender ? (
                                        <S.CreatedMeta>
                                            <S.MetaIcon path={mdiRobotOutline} size={0.55} />
                                            Automated assistant · Created {createdFormatted}
                                        </S.CreatedMeta>
                                    ) : (
                                        <>Created {createdFormatted}</>
                                    )}
                                </S.CreatedDate>
                            )}
                        </S.HeaderTextBlock>
                    </>
                )}
            </S.HeaderLabels>
            <S.HeaderActions>
                {message && (
                    <S.StatusChip status={message.open === false ? 'done' : 'needs_you'}>
                        <S.StatusDot status={message.open === false ? 'done' : 'needs_you'} />
                        {message.open === false ? 'Done' : 'Needs you'}
                    </S.StatusChip>
                )}
                <S.CloseButton onClick={handleCloseClick} type="button" variant="unstyled" aria-label="Close panel">
                    <S.CloseIcon path={mdiClose} size={0.83} />
                </S.CloseButton>
            </S.HeaderActions>
        </S.Header>
    );
};
